import React, { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

export const CalendarComponent = ({ selectedDate, onDateSelect }) => {
  const [calendarMonth, setCalendarMonth] = useState(
    selectedDate ? selectedDate.getMonth() : new Date().getMonth()
  );
  const [calendarYear, setCalendarYear] = useState(
    selectedDate ? selectedDate.getFullYear() : new Date().getFullYear()
  );

  const months = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];

  const years = Array.from(
    { length: 20 },
    (_, i) => new Date().getFullYear() - 5 + i
  );

  const getDaysInMonth = (month, year) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (month, year) => {
    return new Date(year, month, 1).getDay();
  };

  const isToday = (day, month, year) => {
    const today = new Date();
    return (
      today.getDate() === day &&
      today.getMonth() === month &&
      today.getFullYear() === year
    );
  };

  const isSelected = (day, month, year, selectedDate) => {
    if (!selectedDate) return false;
    return (
      selectedDate.getDate() === day &&
      selectedDate.getMonth() === month &&
      selectedDate.getFullYear() === year
    );
  };

  const isPastDate = (day, month, year) => {
    const date = new Date(year, month, day);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
  };

  const handleDateSelect = day => {
    const selectedDate = new Date(calendarYear, calendarMonth, day);
    onDateSelect(selectedDate);
  };

  const navigateMonth = direction => {
    if (direction === 'prev') {
      if (calendarMonth === 0) {
        setCalendarMonth(11);
        setCalendarYear(calendarYear - 1);
      } else {
        setCalendarMonth(calendarMonth - 1);
      }
    } else {
      if (calendarMonth === 11) {
        setCalendarMonth(0);
        setCalendarYear(calendarYear + 1);
      } else {
        setCalendarMonth(calendarMonth + 1);
      }
    }
  };

  return (
    <div className="p-4 bg-white border border-gray-200 rounded-lg shadow-lg min-w-[320px]">
      {/* Calendar Header */}
      <div className="flex items-center justify-between mb-4">
        <button
          type="button"
          onClick={() => navigateMonth('prev')}
          className="p-1 hover:bg-gray-100 rounded transition-colors"
        >
          <ChevronLeft size={20} />
        </button>

        <div className="flex items-center space-x-2">
          <Select
            value={calendarMonth.toString()}
            onValueChange={value => setCalendarMonth(parseInt(value))}
          >
            <SelectTrigger className="w-32 h-8">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {months.map((month, index) => (
                <SelectItem key={index} value={index.toString()}>
                  {month}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select
            value={calendarYear.toString()}
            onValueChange={value => setCalendarYear(parseInt(value))}
          >
            <SelectTrigger className="w-20 h-8">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {years.map(year => (
                <SelectItem key={year} value={year.toString()}>
                  {year}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <button
          type="button"
          onClick={() => navigateMonth('next')}
          className="p-1 hover:bg-gray-100 rounded transition-colors"
        >
          <ChevronRight size={20} />
        </button>
      </div>

      {/* Days of Week */}
      <div className="grid grid-cols-7 gap-1 mb-2">
        {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(day => (
          <div
            key={day}
            className="text-center text-sm font-medium text-gray-500 py-2"
          >
            {day}
          </div>
        ))}
      </div>

      {/* Calendar Grid */}
      <div className="grid grid-cols-7 gap-1">
        {/* Empty cells for days before the first day of the month */}
        {Array.from(
          { length: getFirstDayOfMonth(calendarMonth, calendarYear) },
          (_, i) => (
            <div key={`empty-${i}`} className="h-10"></div>
          )
        )}

        {/* Days of the month */}
        {Array.from(
          { length: getDaysInMonth(calendarMonth, calendarYear) },
          (_, i) => {
            const day = i + 1;
            const isPast = isPastDate(day, calendarMonth, calendarYear);
            const isCurrentDay = isToday(day, calendarMonth, calendarYear);
            const isSelectedDay = isSelected(
              day,
              calendarMonth,
              calendarYear,
              selectedDate
            );

            return (
              <button
                key={day}
                type="button"
                onClick={() => !isPast && handleDateSelect(day)}
                className={`h-10 w-10 text-sm rounded-full transition-colors ${
                  isSelectedDay
                    ? 'bg-black text-white hover:bg-gray-800'
                    : isCurrentDay
                      ? 'bg-gray-100 text-black font-semibold'
                      : isPast
                        ? 'text-gray-300 cursor-not-allowed'
                        : 'hover:bg-gray-100 text-gray-700'
                }`}
                disabled={isPast}
              >
                {day}
              </button>
            );
          }
        )}
      </div>
    </div>
  );
};
